import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import { Popper, Dialog, IconButton, Button } from '@material-ui/core';

// CONSTANTS
import {
  WHITE,
  MAIN_BLACK,
  GRAY_SUIT,
  SANTAS_GRAY,
  SANTAS_GRAY_2,
  PORCELAIN,
  ROYAL_BLUE_2,
  ATHENS_GRAY,
} from '@/constants/colors';

// UTILITIES
import breakpoints from '@/utility/breakpoints';

// STYLES
import { showLogoBackground } from '@/styles/background';

export const TooltipPopperComponent = styled(Popper)`
  ${({ theme, cardwidth, cardheight, tooltipheight, page }) => {
    const isWatchlist = page === 'watchlist';
    const isBigCard = cardwidth > 220;
    const isBigWatchlistCard = isWatchlist && isBigCard;
    const PADDING = 15;
    const borderRadius = isWatchlist ? 8 : 14;
    const contentPadding = isWatchlist && cardwidth > 240 ? '24px 16px' : `${PADDING}px`;
    const MARGIN = isBigWatchlistCard ? 0 : PADDING;

    const bigCardStyle =
      isBigWatchlistCard &&
      css`
        .movie-content {
          &__title {
            margin-bottom: 12px;

            font-weight: 600;
            font-size: 2rem;
            line-height: 2rem;
          }

          &__subtitle,
          &__description {
            margin: 0;

            font-size: 1.4rem;
            line-height: 2rem;
          }

          &__divider {
            margin: 16px 0;
          }
        }
      `;

    return css`
      z-index: 1210;
      margin-top: -${(tooltipheight - cardheight) / 2}px;
      cursor: pointer;

      &[x-placement='left-start'] {
        margin-right: -${(cardwidth + cardwidth + MARGIN) / 2}px;
      }

      &[x-placement='right-start'] {
        margin-left: -${(cardwidth + cardwidth + MARGIN) / 2}px;
      }

      .MuiTooltip-tooltip {
        width: ${cardwidth + MARGIN * 2}px;
        height: ${cardheight + MARGIN * 2}px;
        min-width: 190px;
        min-height: 340px;
        padding: 0;
        margin: 0;
        border-radius: ${borderRadius}px;
        box-shadow: 0 4px 24px 0 ${rgba(MAIN_BLACK, 0.15)};
        background-color: ${theme.palette.showMovieCard.hoverBg};
        color: ${theme.palette.showMovieCard.hoverText};
        display: flex;

        .movie-card__content {
          padding: ${contentPadding};

          ${bigCardStyle}
        }
      }
    `;
  }}
`;

export const ShowMovieCardLogo = styled.div`
  ${({ $background }) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    pointer-events: none;

    background-image: url(${$background.image});
    background-size: ${$background.size};
    background-repeat: no-repeat;
    background-position: center center;
    border-radius: 4px;

    &.movie-card__logo--votes {
      ${showLogoBackground}
    }
  `}
`;

export const ShowMovieCardContainer = styled.div`
  .movie-card,
  &.movie-card {
    position: relative;
    outline: none;
    cursor: pointer;
    width: 100%;

    display: flex;
    flex-direction: column;

    &__logo-container {
      width: 100%;
      height: 0;
      overflow: hidden;
      padding-top: 150%;
      background: ${WHITE};
      position: relative;
      border-radius: 4px;

      .MuiChip-root {
        z-index: 2;
        top: 8px;
        right: 8px;
        position: absolute;
        max-width: calc(100% - 20px);
      }
    }

    &__status {
      position: absolute;
      bottom: 0;
      width: 100%;
      padding: 6px 12px;
      background: ${rgba('#8D8D8D', 0.32)};
      color: ${WHITE};
      font-size: 2rem;
      z-index: 1;
      text-align: right;

      &--watched {
        background: linear-gradient(
          162.4deg,
          rgba(106, 106, 106, 0.576) 3.29%,
          rgba(90, 89, 89, 0.592) 27.49%,
          rgba(70, 70, 70, 0.64) 92.95%
        );
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 4rem;
        cursor: pointer;
      }
    }

    &__main-info {
      flex: 1;

      width: 100%;
      margin-top: 8px;

      display: flex;
      flex-direction: column;
    }

    &__subtitle {
      white-space: wrap;
      overflow: hidden;
      min-height: 1.1em;
      max-height: 3.4em;
    }

    &__year {
      opacity: 0;

      color: ${SANTAS_GRAY_2};
    }

    &__apps-list {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;

      .movie-card__apps {
        width: 100%;
      }

      .content-card__more-btn {
        flex-shrink: 0;
      }
    }

    .movie-votes {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
    }

    &--fullHeight {
      min-height: 100%;
    }

    &--draggable {
      cursor: default;
      filter: drop-shadow(4px 16px 32px ${rgba(MAIN_BLACK, 0.25)});
    }

    &--selectable {
      overflow: hidden;

      .movie-card__logo-container {
        overflow: visible;
      }
    }

    &--loading {
      cursor: default;
    }

    &--tooltip {
      .movie-card__title {
        font-weight: 600;
      }
    }

    &--watchlist {
      padding: 16px;
      background-color: ${ATHENS_GRAY};
      border-radius: 8px;
      cursor: default;

      .movie-card__logo-wrap {
        position: relative;
      }

      .movie-card__logo-container {
        cursor: pointer;
      }

      .movie-card__title {
        line-height: 1.6rem;
        font-weight: 600;
      }

      .movie-card__main-info {
        justify-content: space-between;
      }

      .movie-card__apps {
        margin-top: auto;
      }

      .movie-card__status {
        &--watched {
          top: -16px;
          left: -16px;
          width: calc(100% + 32px);
          padding: 16px;
          border-radius: 8px 8px 0 0;

          ${breakpoints.down('xs')} {
            top: -8px;
            left: -8px;
            width: calc(100% + 16px);
            padding: 8px;
          }
        }
      }

      ${breakpoints.down('xs')} {
        padding: 8px;
      }
    }
  }

  .selecteble-item {
    position: absolute;
    top: -15px;
    left: -15px;
    width: calc(100% + 30px);
    padding: 12px 15px 8px;
    background: linear-gradient(
      91.75deg,
      ${rgba('#EAE9E9', 0.65)} 2.61%,
      ${rgba('#DCD6D6', 0.28)} 99.38%
    );
    z-index: 2;

    ${breakpoints.down('xs')} {
      top: -8px;
      left: -8px;
      width: calc(100% + 16px);
      padding-top: 4px;
      padding-right: 8px;
      padding-left: 8px;
    }

    .MuiCheckbox-root .icon-correct-symbol:before {
      border-color: ${rgba(MAIN_BLACK, 0.4)};
    }
  }
`;

export const ShowMovieCardContentStyled = styled.div`
  ${({ theme: { palette } }) => css`
    padding: 15px;
    height: 100%;

    display: flex;

    .movie-card,
    &.movie-card {
      padding: 0;

      &__year {
        opacity: 1;
      }

      &--tooltip {
        color: ${palette.showMovieCard.hoverText};

        .movie-card__title {
          color: inherit;
        }
      }

      &--dialog {
        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;

        .movie-card__title {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          text-align: center;
        }

        .movie-card__logo-container {
          padding: 0;
          width: 200px;
          height: 300px;
        }

        .movie-card__main-info {
          margin-top: 24px;

          .movie-card__title {
            text-align: center;
          }
        }
      }

      &--loading {
        background-color: ${palette.secondary.main};
      }


    }

    .movie-content,
    &.movie-content {
      &__title {
        font-weight: 600;
        line-height: 1.2;
        margin-bottom: 4px;
      }

      &__subtitle {
        margin-bottom: 4px;
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
      }

      &__description {
        margin-top: 12px;
      }

      &__section {
        width: 100%;
      }

      &__apps {
        margin-top: 8px;
      }

      &__indent {
        margin-top: auto;
      }

      &__watchlist-btn {
        margin-top: 16px;
        padding-right: 8px !important;
        padding-left: 8px !important;
        text-align: center;

        &:disabled {
          background-color: ${GRAY_SUIT};
        }

        .MuiButton-endIcon {
          margin-right: 0;
        }
      }

      &__details-btn {
          margin-top: 8px;
      }

      &--dialog {
        flex-direction: column;
        align-items: center;

        .movie-content__title {
          overflow: initial;
          white-space: initial;
          text-align: center;
          margin-bottom: 16px;
        }

        .movie-content__apps {
          margin-top: 16px;

          .apps-title {
            color: inherit;
            margin-bottom: 8px;
          }
        }

        .movie-content__description {
          width: 100%;
          margin-top: 8px;
        }

        .movie-content__watchlist-btn {
          margin-top: 24px;
        }

        .apps-list {
          .app-card {
            width: 80px;
            height: 50px;
            background-color: ${PORCELAIN};
          }

          .app-card + .app-card {
            margin-left: 8px;
          }
        }
      }

      &--tooltip {
        min-height: 340px;

        .movie-content__section {
          display: flex;
          flex-direction: column;
        }

        .movie-content__subtitle {
          font-size: 12px;
          line-height: 14px;
          word-break: break-word;
        }

        .movie-content__divider {
          background-color: ${SANTAS_GRAY};
          margin-bottom: 8px;
        }

        .movie-content__description {
          color: ${palette.showMovieCard.hoverTextSecondary};
          font-size: 12px;
          line-height: 14px;
          margin-top: 0;
        }

        .movie-content__watchlist-btn {
          font-size: 14px;
          line-height: 14px;
          padding-top: 9px;
          padding-bottom: 9px;
        }

        &.movie-content--watchlist {
          .movie-content {
            &__apps {
            .apps-title {
              color: ${GRAY_SUIT};
            }

            & + .movie-content__indent {
              margin: 0;
            }
          }

          &__subtitle,
          &__description {
            font-size: 1.3rem;
          }
        }
      }
    }
  `}
`;

export const DialogStyled = styled(Dialog)`
  z-index: 1250 !important;

  .MuiDialog-paperFullWidth {
    border-radius: 16px;
    max-width: 1190px;
  }

  .MuiDialogContent-root {
    padding: 0;
  }
`;

export const RemoveItemButton = styled(IconButton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 32px;
  height: 32px;
  border-radius: 4px 0px 8px;
  background-color: ${rgba(MAIN_BLACK, 0.5)};

  svg {
    font-size: 2.4rem;
    color: ${WHITE};
  }

  &:hover {
    background-color: ${rgba(MAIN_BLACK, 0.3)};
  }
`;

export const CloseIcon = styled(IconButton)`
  z-index: 2;
  position: absolute;
  top: 5px;
  right: 5px;

  font-size: 1.5rem;
`;

export const DetailsButtonStyled = styled(Button)`
  background-color: ${ROYAL_BLUE_2};

  &:hover {
    background-color: ${rgba(ROYAL_BLUE_2, 0.9)};
  }
`;

export const DraggableItem = styled.div`
  .draggable-item,
  &.draggable-item {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    cursor: default;

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
    }

    &__zone {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 33px;
      background-color: ${WHITE};
      display: flex;
      justify-content: center;
      align-items: center;

      cursor: grab;

      &:active {
        cursor: grabbing;
      }

      svg {
        font-size: 3.6rem;
      }
    }
  }
`;

export const AppsListStyled = styled.div`
  .apps-title {
    color: ${WHITE};
    margin-bottom: 4px;
  }

  .apps-list {
    display: flex;
    min-height: 36px;

    .app-card {
      width: 45px;
      height: 32px;
      border-radius: 4px;
      background-color: ${({ theme: { palette } }) => palette.common.white};

      background-size: cover;
      background-repeat: no-repeat;
      background-position: center center;
      //pointer-events: none;

      display: flex;
      align-items: center;
      justify-content: center;
    }

    .app-card + .app-card {
      margin-left: 4px;
    }
  }

  &.apps {
    &--default {
      .apps-title {
        margin-top: 12px;
        color: ${SANTAS_GRAY};
      }
    }

    &--default,
    &--tooltip {
      .apps-list {
        width: calc(100% + 6px);
        margin: -3px;
        padding: 3px;

        .MuiGrid-container {
          width: calc(100% + 6px);
          margin: -3px;
        }

        .MuiGrid-item {
          padding: 2px;
        }
      }
    }
  }
`;

export default ShowMovieCardContainer;
